import {
  Button,
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  px,
  SelectField,
  simpleHeader,
  TextField,
  Tooltip,
  useModal,
} from "@homebound/beam";
import { useSuspense } from "@rest-hooks/react";
import { useMemo, useState } from "react";
import { UwConfigOptionsEndpoint, UwConfigRow, UwConfigRowsEndpoint } from "./UwConfigEndpoints";
import { formatAttribution, formatDate, UwConfigRowModal } from "./UwConfigRowModal";
import { UwConfigImportExport } from "./UwConfigImportExport";
import { formatValue, UwConfigTypeDef } from "./uwConfigFields";

const ALL_METROS = "__all__";

type UwConfigTableProps = {
  typeDef: UwConfigTypeDef;
};

export function UwConfigTable({ typeDef }: UwConfigTableProps) {
  const { rows, version } = useSuspense(UwConfigRowsEndpoint, { configType: typeDef.type });
  const { options } = useSuspense(UwConfigOptionsEndpoint, { configType: typeDef.type });
  const { openModal } = useModal();
  const [metro, setMetro] = useState<string>(ALL_METROS);
  const [search, setSearch] = useState<string | undefined>();

  const metroOptions = useMemo(() => {
    const metros = [...new Set(rows.map((r) => r.metro ?? "default"))].sort();
    return [{ id: ALL_METROS, name: "All metros" }, ...metros.map((m) => ({ id: m, name: m }))];
  }, [rows]);

  // filtered here rather than with the table's `filter` so the row count matches what's shown
  const filteredRows = useMemo(() => {
    const terms = (search ?? "").toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
      if (metro !== ALL_METROS && (r.metro ?? "default") !== metro) return false;
      const text = [...typeDef.fields.map((f) => formatValue(f, r[f.name])), r.updated_by ?? ""]
        .join(" ")
        .toLowerCase();
      return terms.every((term) => text.includes(term));
    });
  }, [rows, metro, search, typeDef]);

  const columns = useMemo(
    () =>
      createColumns(typeDef, (row) =>
        openModal({ content: <UwConfigRowModal typeDef={typeDef} options={options} row={row} /> }),
      ),
    [typeDef, options, openModal],
  );

  return (
    <div css={Css.dg.gap2.mw0.$}>
      <div css={Css.gray700.sm.$}>{typeDef.description}</div>
      <div css={Css.df.aife.jcsb.gap2.fww.$}>
        <div css={Css.df.aife.gap2.$}>
          <div css={Css.w(px(280)).$}>
            <SelectField
              label="Metro"
              value={metro}
              onSelect={(v) => setMetro(v ?? ALL_METROS)}
              options={metroOptions}
            />
          </div>
          <div css={Css.w(px(280)).$}>
            <TextField label="Search" value={search} onChange={setSearch} clearable />
          </div>
          <div css={Css.sm.gray700.pb1.$} data-testid="rowCount">
            {filteredRows.length} of {rows.length} rows · version {version}
          </div>
        </div>
        <div css={Css.df.gap1.$}>
          <Button
            label="Add Row"
            icon="plus"
            onClick={() => openModal({ content: <UwConfigRowModal typeDef={typeDef} options={options} /> })}
          />
          <UwConfigImportExport typeDef={typeDef} />
        </div>
      </div>
      {/* only the table scrolls, so the filters and buttons above stay in view however wide the config is */}
      <div css={Css.oa.mw0.maxh("calc(100vh - 320px)").$} data-testid="tableScroll">
        <GridTable
          columns={columns}
          rows={createRows(filteredRows)}
          stickyHeader
          sorting={{ on: "client" }}
          fallbackMessage="No rows match"
        />
      </div>
    </div>
  );
}

type Row = { kind: "header" } | { kind: "data"; data: UwConfigRow };

function createRows(rows: UwConfigRow[]): GridDataRow<Row>[] {
  return [simpleHeader, ...rows.map((r) => ({ kind: "data" as const, id: `${r.id}`, data: r }))];
}

function createColumns(typeDef: UwConfigTypeDef, onEdit: (row: UwConfigRow) => void): GridColumn<Row>[] {
  return [
    // first and pinned (with the fields that identify a row) so editing never needs a horizontal scroll
    column<Row>({
      id: "edit",
      header: "",
      data: (row) => <Button variant="text" label="Edit" icon="pencil" onClick={() => onEdit(row)} />,
      clientSideSort: false,
      sticky: "left",
      w: "90px",
    }),
    ...typeDef.fields.map((f) =>
      column<Row>({
        id: f.name,
        header: f.label,
        data: (row) => {
          const text = formatValue(f, row[f.name]);
          return {
            content: () => (
              <Tooltip title={formatAttribution(row.field_updates[f.name])}>
                <span>{text}</span>
              </Tooltip>
            ),
            value: text,
          };
        },
        ...(f.pinned
          ? { sticky: "left" as const, w: "220px" }
          : { w: f.kind === "text" ? 2 : 1, mw: f.kind === "text" ? "160px" : "100px" }),
      }),
    ),
    column<Row>({
      id: "updated",
      header: "Last Changed",
      data: (row) => ({
        content: (
          <div css={Css.xs.$}>
            <div>{row.updated_by ?? "Original config"}</div>
            <div css={Css.gray700.$}>{formatDate(row.updated_at)}</div>
          </div>
        ),
        value: row.updated_by ?? "",
        sortValue: row.updated_at,
      }),
      w: "200px",
    }),
  ];
}
